import { useEffect, useState } from 'react';
import { Grid, MenuItem } from '@mui/material';
import { ExpansionPanel, InputField } from '@/components/common';
import { useCompanyAnnualStore } from '@/store/audit/companyAnnualStore';
import type { CompanyAnnual } from '@/services/accountService/companyAnnualService';

type Props = {
  annualId: number;
  onChange?: (patch: Partial<CompanyAnnual>) => void;
  showFirstAudit?: boolean;
  forceFirstAudit?: boolean;
  externalErrors?: {
    qualifyOpinion?: boolean;
  };
  disableAccordion?: boolean;
  initialData?: { qualifyOpinion?: number; firstAudit?: number } | null;
};

export default function EpAuditOpinion({ annualId, onChange, showFirstAudit = true, forceFirstAudit = false, externalErrors, disableAccordion = false, initialData }: Props) {
  const getAuditOpinionInfo = useCompanyAnnualStore(s => s.getAuditOpinionInfo);
  const storeAnnual = useCompanyAnnualStore(s => s.byAnnualId[Number(annualId)]);
  const [local, setLocal] = useState<{ qualifyOpinion?: number; firstAudit?: number }>({});

  useEffect(() => {
    if (initialData) {
      setLocal(initialData);
      return;
    }

    let mounted = true;
    (async () => {
      const v = await getAuditOpinionInfo(annualId);
      if (!mounted) return;
      setLocal(v);
    })();
    return () => { mounted = false; };
  }, [annualId, getAuditOpinionInfo, initialData]);

  // Reactively sync from store when data changes (e.g. from Content Sheet import)
  useEffect(() => {
    if (!storeAnnual) return;
    setLocal(prev => {
      const nextQO = storeAnnual.qualifyOpinion ?? prev.qualifyOpinion;
      const nextFA = storeAnnual.firstAudit ?? prev.firstAudit;
      if (nextQO === prev.qualifyOpinion && nextFA === prev.firstAudit) return prev;
      return { qualifyOpinion: nextQO, firstAudit: nextFA };
    });
  }, [storeAnnual]);

  useEffect(() => {
    if (forceFirstAudit && local.firstAudit !== 1) {
      emit({ firstAudit: 1 });
    }
  }, [forceFirstAudit, local.firstAudit]);

  const emit = (patch: Partial<typeof local>) => {
    const next = { ...local, ...patch };
    setLocal(next);
    onChange?.(patch as Partial<CompanyAnnual>);
  };

  const parseSelectNumber = (v: any): number | undefined => {
    if (v === '' || v === null || v === undefined) return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  return (
    <ExpansionPanel 
      title="Audit Opinion (審計意見)"
      expanded={disableAccordion ? true : undefined}
      onChange={disableAccordion ? () => {} : undefined}
    >
      <Grid container spacing={0.5}>
        {showFirstAudit && (
          <Grid size={6 as any}>
            <InputField select fullWidth variant="outlined" label="First time audit? (是否首次審計)" value={forceFirstAudit ? 1 : (local.firstAudit ?? '')} display={forceFirstAudit} onChange={(e) => emit({ firstAudit: parseSelectNumber(e.target.value) as any })}>
              <MenuItem dense value="">Select</MenuItem>
              <MenuItem dense value={0}>No</MenuItem>
              <MenuItem dense value={1}>Yes</MenuItem>
            </InputField>
          </Grid>
        )}
        <Grid size={6 as any}>
          <InputField 
            select 
            fullWidth 
            variant="outlined" 
            label="Qualify opinion? (審計意見)" 
            value={local.qualifyOpinion ?? ''} 
            onChange={(e) => emit({ qualifyOpinion: parseSelectNumber(e.target.value) as any })}
            error={externalErrors?.qualifyOpinion}
            helperText={externalErrors?.qualifyOpinion ? 'Qualify opinion is required' : undefined}
          >
            <MenuItem dense value="">Select</MenuItem>
            <MenuItem dense value={0}>Unqualified</MenuItem>
            <MenuItem dense value={1}>Qualified</MenuItem>
          </InputField>
        </Grid>
      </Grid>
    </ExpansionPanel>
  );
}
